// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getAllManagementGroupOptions from "@guestsupply/App/Handlers/GroupManagementOptions/GetAllManagementGroupOptions";
import getManagementGroupDetails from "@guestsupply/App/Handlers/GroupManagementOptions/GetManagementGroupDetails";
import updateManagementGroupDetails from "@guestsupply/App/Handlers/GroupManagementOptions/UpdateManagementGroupDetails";
import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { ManagementGroupOptionsModel } from "@guestsupply/App/Services/ManagementGroupOptionsService";
import Button from "@guestsupply/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@guestsupply/MobiusOverrides/Button/Props";
import TextField, { TextFieldProps } from "@guestsupply/MobiusOverrides/TextField/TextField";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import ToasterContext, { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    getManagementGroupDetails,
    getAllManagementGroupOptions,
    updateManagementGroupDetails,
    xCommonLoadingOverlay,
};

export interface ManagementGroupConfigurationsStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    groupDynamicDropdown?: DynamicDropdownPresentationProps;
    form?: InjectableCss;
    checkboxStyle?: CheckboxPresentationProps;
    minimumOrderTextField?: TextFieldProps;
    actionButtons?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

export const managementGroupConfigurationsStyles: ManagementGroupConfigurationsStyles = {};
const styles = managementGroupConfigurationsStyles;

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

const MGConfigurations = ({
    getManagementGroupDetails,
    getAllManagementGroupOptions,
    updateManagementGroupDetails,
    xCommonLoadingOverlay,
}: Props) => {
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedGroupName, setSelectedGroupName] = useState<string | undefined>("");
    const [selectedGroupDetails, setSelectedGroupDetails] = useState<ManagementGroupOptionsModel | null>(null);
    const toasterContext = React.useContext(ToasterContext);
    React.useEffect(() => {
        xCommonLoadingOverlay({ key: "add", value: "MGConfiguration" });
        const groups = getAllManagementGroupOptions({
            onSuccess: result => {
                const newOptions = result.map(option => ({
                    optionText: option,
                    optionValue: option,
                    searchString: option,
                }));
                setOptions(newOptions);
                xCommonLoadingOverlay({ key: "clear", value: "MGConfiguration" });
            },
            onError: errorMessage => {
                xCommonLoadingOverlay({ key: "clear", value: "MGConfiguration" });
                toasterContext.addToast({
                    body: errorMessage,
                    messageType: "danger",
                });
            },
        });
    }, []);

    const onSelectionChangeHandler = (value?: string) => {
        if (selectedGroupName !== value) {
            getManagementGroupDetails({
                name: value,
                onSuccess: result => {
                    setSelectedGroupDetails(result);
                    setSelectedGroupName(value);
                },
                onError: errorMessage => {
                    toasterContext.addToast({
                        body: errorMessage,
                        messageType: "danger",
                    });
                },
            });
        }
    };

    const limitSearchChangeHandler: CheckboxProps["onChange"] = (_, checked) => {
        if (selectedGroupDetails !== null) {
            setSelectedGroupDetails({ ...selectedGroupDetails, limitSearchCatalog: checked });
        }
    };

    const minimumOrderAmountChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (selectedGroupDetails !== null) {
            setSelectedGroupDetails({ ...selectedGroupDetails, minimumOrderAmount: Number(e.currentTarget.value) });
        }
    };

    const showInvoicesChangeHandler: CheckboxProps["onChange"] = (_, checked) => {
        if (selectedGroupDetails !== null) {
            setSelectedGroupDetails({ ...selectedGroupDetails, showInvoicesOnline: checked });
        }
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedGroupDetails !== null && selectedGroupName !== "") {
            xCommonLoadingOverlay({ key: "add", value: "MGConfiguration" });
            updateManagementGroupDetails({
                name: selectedGroupName!,
                details: selectedGroupDetails,
                onSuccess: () => {
                    xCommonLoadingOverlay({ key: "clear", value: "MGConfiguration" });
                    toasterContext.addToast({
                        body: translate("Group Details Have Been Updated Successfully"),
                        messageType: "success",
                    });
                },
                onError: (errorMessage: string) => {
                    xCommonLoadingOverlay({ key: "clear", value: "MGConfiguration" });
                    toasterContext.addToast({ body: translate("Group Details Update Failed"), messageType: "danger" });
                },
            });
        }
    };

    const StyledForm = getStyledWrapper("form");
    const formId = "MGFormId";

    return (
        <StyledWrapper {...styles.container} data-test-selector="MG-flexContainer">
            <StyledWrapper {...styles.wrapper} data-test-selector="MG-wrapper">
                <DynamicDropdown
                    {...styles.groupDynamicDropdown}
                    label={translate("MG Options - Group Configuration")}
                    onSelectionChange={onSelectionChangeHandler}
                    selected={selectedGroupName}
                    options={options}
                    placeholder={translate("Enter keyword or item #")}
                />

                <StyledForm onSubmit={handleFormSubmit} {...styles.form}>
                    <Checkbox
                        {...styles.checkboxStyle}
                        checked={selectedGroupDetails?.limitSearchCatalog || false}
                        onChange={limitSearchChangeHandler}
                        disabled={!selectedGroupDetails}
                    >
                        {translate("Limit Search Catalog")}
                    </Checkbox>

                    <TextField
                        {...styles.minimumOrderTextField}
                        type="number"
                        name={`MG-OptionsGroup${selectedGroupName}`}
                        value={selectedGroupDetails?.minimumOrderAmount || 0}
                        disabled={!selectedGroupDetails}
                        onChange={minimumOrderAmountChangeHandler}
                        required
                        maxLength={5}
                        {...{ formId }}
                    />
                    <Checkbox
                        {...styles.checkboxStyle}
                        checked={selectedGroupDetails?.showInvoicesOnline || false}
                        onChange={showInvoicesChangeHandler}
                        disabled={!selectedGroupDetails}
                    >
                        {translate("Show Online Invoices")}
                    </Checkbox>
                    <StyledWrapper {...styles.actionButtons}>
                        <Button {...styles.submitButton} disabled={!selectedGroupDetails} type="submit">
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(MGConfigurations),
    definition: {
        group: "MG Options" as any,
        displayName: "MG-Options Configurations",
        allowedContexts: ["ManagementGroupOptionsPage"],
    },
};

export default widgetModule;
